import * as React from 'react';

import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';
import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { t } from 'utils/language';
import { CUSTOM_LIST_TYPE } from 'utils/list';

import { isNotEmpty, maxLength } from 'utils/validators';
import type { ListMeta } from 'types';

const LABEL_MAX_LENGTH = 60;

export type Props = {
  label?: string,
  description?: string,
  title: string,
  isEditMode?: boolean,
  organizationId: number,
  listId?: number,
  onModalClose?: () => void,
  onCloseModal: () => void,
  onSave?: (a?: string | null) => void,
  onSaveSearchToList?: (c: string, b: string, a: number) => void,
  dispatchSaveList: (list: ListMeta, organizationId: number, listId?: number | null, shouldUpdate?: boolean | null) => Promise<void>
};

type State = {
  label: string,
  description: string,
  isModified: boolean
};

class ListsCreateEditDummyModal extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      label: props.label || '',
      description: props.description || '',
      isModified: false
    };
  }

  componentWillUnmount() {
    const { onModalClose } = this.props;

    if (onModalClose) {
      onModalClose();
    }
  }

  handleFieldChange = (event: Event): void => {
    const target = event.currentTarget;
    const value = ((target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) && target.value) || '';
    const key = (target instanceof HTMLElement && target.dataset.key) as ('label' | 'description');

    if (key) {
      const newState = {
        [key]: value,
        isModified: true
      } as State;

      this.setState(newState);
    }
  };

  handleSubmitList = (): void => {
    const {
      organizationId,
      listId,
      isEditMode,
      dispatchSaveList,
      onCloseModal,
      onSave,
      onSaveSearchToList
    } = this.props;

    const { label, description } = this.state;
    const type = CUSTOM_LIST_TYPE;

    if (onSaveSearchToList) {
      onSaveSearchToList(label, description, organizationId);
      onCloseModal();
      return;
    }

    dispatchSaveList({ label, description }, organizationId, listId, isEditMode)
      .then(() => {
        if (onSave) {
          onSave(type);
        }

        onCloseModal();
      });
  };

  render(): React.ReactNode {
    const { title, onCloseModal } = this.props;
    const { label, description, isModified } = this.state;

    return (
      <ModalFrame
        title={title}
      >
        {
          isModified &&
          <PromptExit type="modal" />
        }
        <ModalFrameInner
          narrowContent
        >
          <Form onSubmit={this.handleSubmitList}>
            <FormField
              label={t('lists.modal_field_list_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              showValidationIcon={false}
              validators={[
                isNotEmpty(label),
                maxLength(label, LABEL_MAX_LENGTH)
              ]}
            >
              <Input
                name="list-label"
                value={label}
                width="responsive"
                placeholder={t('lists.modal_field_list_placeholder')}
                data={{ key: 'label' }}
                onChange={this.handleFieldChange}
                maxLength={LABEL_MAX_LENGTH}
                focusOnMount
              />
            </FormField>

            <FormField
              label={t('lists.modal_field_description_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showValidationIcon={false}
            >
              <Input
                name="list-description"
                value={description}
                width="responsive"
                multiline
                placeholder={t('lists.modal_field_description_placeholder')}
                data={{ key: 'description' }}
                onChange={this.handleFieldChange}
              />
            </FormField>

            <ModalFrameFooter>
              <ActionButton
                color="tertiary"
                onClick={onCloseModal}
              >
                {t('lists.button_cancel')}
              </ActionButton>
              <ActionButton
                type="submit"
              >
                {t('lists.button_save_list')}
              </ActionButton>
            </ModalFrameFooter>
          </Form>
        </ModalFrameInner>
      </ModalFrame>
    );
  }
}

export default ListsCreateEditDummyModal;
